import React, { useCallback } from 'react';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { useTranslator } from 'tradera/lang/translate';
import { useScrollToTopOnMount } from 'tradera/hooks/use-scroll-to-top-on-mount';
import { usePageMeta } from 'tradera/hooks/use-page-meta';
import { ContentSwitch } from 'tradera/components/content-switch/content-switch';
import QuickView from 'tradera/components/quick-view/quick-view-modal';
import { Recommendations } from 'tradera/components/recommendations/recommendations';
import Flower from '@tradera/blueprint/theme/icons/flower.svg?jsx';
import Warning from '@tradera/blueprint/theme/icons/warning.svg?jsx';
import { LayoutC } from 'tradera/apps/tradera/layout/layout-c';
import { useRecommendationsRequestBuilder } from 'tradera/components/recommendations/recommendations-requests';
import type { match } from 'react-router';
import type { History } from 'history';
import { useAppSelector } from 'tradera/state/hooks';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';

const ITEMS_PER_PAGE = 36;

const RECOMMENDATION_MODES = [
    {
        key: 'selected',
        gaCategory: 'Endless Recommendation',
        label: 'endlessRecommendation_selected',
        slotId: 'Infinite_scroll_recommendation_slot'
    },
    {
        key: 'endingSoon',
        gaCategory: 'Endless Recommendation Ending Soon',
        label: 'endlessRecommendation_endingSoon',
        slotId: 'Last_chance_infinite_scroll'
    }
];

type RecommendationsPageProps = {
    history: History;
    match: match<{ recommendationModeId: string }>;
    pageType: string;
};

export const RecommendationsPage = ({
    history,
    match,
    pageType
}: RecommendationsPageProps) => {
    const isLoggedIn = useIsLoggedIn();
    const {
        params: { recommendationModeId }
    } = match;
    usePageMeta(pageType);
    const { t } = useTranslator();
    useScrollToTopOnMount();

    const shippingCountryCode = useAppSelector(
        selectShippingRegionCountryCodeIso2
    );
    const onSwitchMode = useCallback(
        (mode: string) => {
            history.replace(`/ma/endless-recommendation/${mode}`);
        },
        [history]
    );

    const mainContent = (
        <>
            <h1 className="heading-madrid text-styled text-center mt-4">
                {isLoggedIn
                    ? t('endlessRecommendation_justForYou')
                    : t('endlessRecommendation_trendingOnTradera')}
            </h1>

            <div className="mr-n2 mx-lg-n3 mx-xl-n2">
                <ContentSwitch
                    className="pt-md-2"
                    onSwitch={onSwitchMode}
                    defaultSelected={recommendationModeId}
                    options={RECOMMENDATION_MODES.map((mode) => {
                        return {
                            key: mode.key,
                            node: (
                                <RecommendationsWithRequestUrl
                                    slotId={mode.slotId}
                                    shippingCountryCode={shippingCountryCode}
                                    recommendationsFactory={(
                                        createRequestUrl
                                    ) => (
                                        <Recommendations
                                            createRequestUrl={createRequestUrl}
                                            errorFallback={<ErrorMessage />}
                                            gtmLabel="Endless Recommendations"
                                            noMoreItemsMarker={
                                                <NoMoreItemsMessage />
                                            }
                                            onReachedEnd={trackReachedEndOfRecommendation(
                                                mode.gaCategory
                                            )}
                                        />
                                    )}
                                />
                            ),
                            label: t(mode.label)
                        };
                    })}
                />
            </div>
            <QuickView />
        </>
    );

    return <LayoutC mainContent={mainContent} />;
};

type RecommendationsWithRequestUrlProps = {
    slotId: string;
    recommendationsFactory: (
        createRequestUrl: (pageIndex?: number) => string
    ) => JSX.Element;
    shippingCountryCode: string;
};
const RecommendationsWithRequestUrl = ({
    slotId,
    recommendationsFactory,
    shippingCountryCode
}: RecommendationsWithRequestUrlProps) => {
    const createRequestUrl = useRecommendationsRequestBuilder(
        slotId,
        ITEMS_PER_PAGE,
        shippingCountryCode
    );
    return recommendationsFactory(createRequestUrl);
};

const NoMoreItemsMessage = () => {
    const { t } = useTranslator();
    return (
        <div
            className="col-12 text-center py-5"
            data-recommendations-no-more-items>
            <Flower className="icon icon-xl icon-dark mb-2" />
            <p className="size-paris mb-3">
                {t('endlessRecommendation_noMoreItemsHeading')}
            </p>
            <p className="my-2">{t('endlessRecommendation_noMoreItemsText')}</p>
        </div>
    );
};

const ErrorMessage = () => {
    const { t } = useTranslator();
    return (
        <div className="col-12 text-center py-5" data-recommendations-error>
            <Warning className="icon icon-lg icon-dark mb-1" />
            <p className="size-paris mb-3">
                {t('endlessRecommendation_errorHeading')}
            </p>
            <p className="my-2">{t('endlessRecommendation_errorText')}</p>
            <p className="my-2">
                {t('endlessRecommendation_errorTryAgainLater')}
            </p>
            <div className="col-12 text-center my-4">
                <a
                    data-recommendations-reload
                    role="button"
                    className="btn btn-primary text-white size-paris"
                    href={window.location.href}>
                    {t('endlessRecommendation_errorRecommendedAction')}
                </a>
            </div>
        </div>
    );
};

const trackReachedEndOfRecommendation =
    (category: string) => (itemIds: number[]) => {
        GtmService.trackAction(
            category,
            'Scroll reached end',
            'Number of results',
            itemIds.length
        );
    };
